import { X } from 'lucide-react';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';

import { Input } from '../../components/ui/input.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Label } from '../../components/ui/label.tsx';
import { cn } from '../../lib/utils.ts';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectValue,
  SelectTrigger,
} from '../../components/ui/select.tsx';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover.tsx';
import { Calendar } from '../../components/ui/calendar.tsx';
import { toast } from 'react-toastify';
import {
  useCreateTerm,
  useDeleteTerm,
  useUpdateTerm,
} from '../../api/term/termQueries.js';

export function CreateTermModal({
  open,
  onOpenChange,
  sessions,
  term,
  setTerm,
}) {
  const [termDetails, setTermDetails] = useState({
    sessionId: '',
    termName: '',
    startDate: new Date(),
    endDate: new Date(),
  });
  const [isStartDateOpen, setIsStartDateOpen] = useState(false);
  const [isEndDateOpen, setIsEndDateOpen] = useState(false);
  const { mutate: createTerm, isPending: isCreateTermLoading } =
    useCreateTerm();

  const { mutate: updateTerm, isPending: isUpdateTermLoading } =
    useUpdateTerm();

  const { mutate: deleteTerm, isPending: isDeleteTermLoading } =
    useDeleteTerm();

  useMemo(() => {
    let newStartDate, newEndDate;
    if (
      term !== null &&
      term.startDate !== undefined &&
      term.endDate !== undefined
    ) {
      newStartDate = new Date(term.startDate);
      newEndDate = new Date(term.endDate);
    }
    setTermDetails({
      sessionId: term?.sessionId || '',
      termName: term?.name || '',
      startDate: newStartDate || new Date(),
      endDate: newEndDate || new Date(),
    });
  }, [term]);

  const validateTermEntry = () => {
    // Todo: Check the start date is before the end date - Done
    if (termDetails.startDate.getTime() > termDetails.endDate.getTime()) {
      toast.error('Start date must be before end date');
      return false;
    }

    if (!termDetails.sessionId || !termDetails.termName) {
      toast.error('Please complete all fields');
      return false;
    }
    // Todo: Check if the range is the session start and date - Done
    const selectedSession = sessions.find(
      (s) => s.sessionId === termDetails.sessionId
    );

    if (selectedSession) {
      const sessionStartDate = new Date(selectedSession.startDate);
      const sessionEndDate = new Date(selectedSession.endDate);

      if (
        termDetails.startDate.getTime() < sessionStartDate.getTime() ||
        termDetails.endDate.getTime() > sessionEndDate.getTime()
      ) {
        toast.error(
          `Term dates must be within the session dates: ${format(
            sessionStartDate,
            'PPP'
          )} - ${format(sessionEndDate, 'PPP')}`
        );
        return false;
      }
    }
    return true;
  };

  const handleCreate = () => {
    validateTermEntry();
    // Call Api to create term
    if (validateTermEntry()) {
      createTerm({
        sessionId: termDetails.sessionId,
        name: termDetails.termName,
        startDate: termDetails.startDate,
        endDate: termDetails.endDate,
      });
      setTerm(null);
      onOpenChange(false);
    }
  };

  const handleUpdate = () => {
    validateTermEntry();

    updateTerm({
      termId: term.termId,
      termData: {
        sessionId: termDetails.sessionId,
        name: termDetails.termName,
        startDate: termDetails.startDate,
        endDate: termDetails.endDate,
      },
    });

    setTerm(null);
    onOpenChange(false);
  };

  return (
    <div
      className={`fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 ${
        open ? 'block' : 'hidden'
      }`}
    >
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md px-10 pt-7 pb-4">
        <div className=" mb-4 flex flex-row justify-between items-center px-2 py-2 pt-4">
          <h2 className="text-xl font-semibold ">Enter Term Details</h2>
          <div className="self-center  ">
            <X
              onClick={() => {
                onOpenChange(false);
                setTerm(null);
              }}
              className="text-gray-500 hover:text-gray-700 cursor-pointer block"
            />
          </div>
        </div>

        <div className="overflow-scroll">
          <div className="grid gap-2 flex-1 my-4 px-2">
            <Label htmlFor="eventType">Select Session</Label>
            <Select
              value={termDetails.sessionId}
              onValueChange={(value) =>
                setTermDetails({ ...termDetails, sessionId: value })
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select Session" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Select Session</SelectLabel>
                  {sessions.map((session) => (
                    <SelectItem
                      key={session.sessionId}
                      value={session.sessionId}
                    >
                      {session.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2 flex-1 my-4 px-2">
            <Label>Enter Term Name</Label>
            <Input
              placeholder="e.g. First Term"
              value={termDetails.termName}
              onChange={(e) =>
                setTermDetails({ ...termDetails, termName: e.target.value })
              }
            />
          </div>

          <div className="grid gap-2 flex-1 my-4 px-2">
            <Label>Select Start Date</Label>
            <Popover open={isStartDateOpen} onOpenChange={setIsStartDateOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant={'outline'}
                  className={cn(
                    'justify-start text-left font-normal',
                    !termDetails.startDate && 'text-muted-foreground'
                  )}
                >
                  {termDetails.startDate ? (
                    format(termDetails.startDate, 'PPP')
                  ) : (
                    <span>Pick a date</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-auto p-0"
                align="start"
                side="bottom"
              >
                <Calendar
                  mode="single"
                  selected={new Date()}
                  onSelect={(date) => {
                    if (date) {
                      const newDate = date;
                      newDate.setHours(new Date().getHours());
                      newDate.setMinutes(new Date().getMinutes());
                      setTermDetails({ ...termDetails, startDate: newDate });
                      setIsStartDateOpen(false);
                    }
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="grid gap-2 flex-1 my-4 px-2">
            <Label>Select End Date</Label>
            <Popover open={isEndDateOpen} onOpenChange={setIsEndDateOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant={'outline'}
                  className={cn(
                    'justify-start text-left font-normal',
                    !termDetails.endDate && 'text-muted-foreground'
                  )}
                >
                  {termDetails.endDate ? (
                    format(termDetails.endDate, 'PPP')
                  ) : (
                    <span>Pick a date</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-auto p-0"
                align="start"
                side="bottom"
              >
                <Calendar
                  mode="single"
                  selected={new Date()}
                  onSelect={(date) => {
                    if (date) {
                      const newDate = date;
                      newDate.setHours(new Date().getHours());
                      newDate.setMinutes(new Date().getMinutes());
                      setTermDetails({ ...termDetails, endDate: newDate });
                      setIsEndDateOpen(false);
                    }
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex gap-3 justify-end px-2">
            <Button
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                setTerm(null);
              }}
            >
              Cancel
            </Button>
            {term == null ? (
              <Button
                className="bg-purple-600 hover:bg-purple-700 text-white"
                onClick={handleCreate}
                disabled={isCreateTermLoading}
              >
                {isCreateTermLoading ? 'Loading...' : 'Create Term'}
              </Button>
            ) : (
              <>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={() => {
                    deleteTerm(term.termId);
                    setTerm(null);
                    onOpenChange(false);
                  }}
                >
                  {isDeleteTermLoading ? 'Loading...' : ' Delete Term'}
                </Button>
                <Button onClick={handleUpdate}>
                  {isUpdateTermLoading ? 'Loading...' : ' Update Term'}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateTermModal;
